import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as THREE from 'three';
import { ACCENT, DEEP, GRAY, MODELS, N, PN, rng } from '../models/formations';
import { inspectParticle } from '../models/samples';

function boundedStage(stage, model) {
  return Number.isFinite(stage) ? Math.max(0, Math.min(MODELS[model].CAM.length - 1, Math.trunc(stage))) : 0;
}

const ModelPipeline3D = forwardRef(function ModelPipeline3D({
  model = 'fabric', stage = 0, active = true, cameraScale = 1,
  interactionMode = 'page', onPick, onViewChange,
}, ref) {
  const modelKey = Object.prototype.hasOwnProperty.call(MODELS, model) ? model : 'fabric';
  const safeCameraScale = Number.isFinite(cameraScale) && cameraScale > 0 ? cameraScale : 1;
  const hostRef = useRef(null);
  const apiRef = useRef({ model: modelKey, stage: 0, active, selected: -1, onPick });
  const renderRef = useRef(null);
  const controlsRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);
  apiRef.current.model = modelKey;
  // Summary stages reuse the final 3D formation, but have their own samples.
  apiRef.current.stage = Number.isFinite(stage) ? Math.max(0, Math.trunc(stage)) : 0;
  apiRef.current.active = active;
  apiRef.current.cameraScale = safeCameraScale;
  apiRef.current.onPick = onPick;
  apiRef.current.onViewChange = onViewChange;
  apiRef.current.interactionMode = interactionMode;

  useImperativeHandle(ref, () => ({
    zoomBy: (factor) => controlsRef.current?.zoomBy(factor),
    resetView: () => controlsRef.current?.resetView(),
    rotateBy: (yawDelta, pitchDelta = 0) => controlsRef.current?.rotateBy(yawDelta, pitchDelta),
    selectSample: (index) => controlsRef.current?.selectSample(index),
  }), []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const cfg = MODELS[modelKey];
    apiRef.current.selected = -1;
    let dead = false;
    let failed = false;
    let raf = 0;
    let renderer;
    let observer;
    const disposables = new Set();
    const removers = [];
    const own = (resource) => {
      disposables.add(resource);
      return resource;
    };
    const listen = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      removers.push(() => target.removeEventListener(type, handler, options));
    };
    const dispose = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      observer?.disconnect();
      observer = null;
      removers.splice(0).forEach((remove) => remove());
      disposables.forEach((resource) => resource.dispose());
      disposables.clear();
      renderer?.dispose();
      renderer = null;
      renderRef.current = null;
      controlsRef.current = null;
      host.replaceChildren();
    };
    const showFallback = () => {
      if (dead || failed) return;
      failed = true;
      dispose();
      setUnavailable(true);
    };

    const boot = () => {
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'display:block;width:100%;height:100%;touch-action:pan-y;cursor:grab';
      canvas.style.touchAction = apiRef.current.interactionMode === 'viewer' ? 'none' : 'pan-y';
      host.appendChild(canvas);
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      let reducedMotion = Boolean(motionQuery?.matches);
      const scene = new THREE.Scene();
      const cam = new THREE.PerspectiveCamera(38, 1, 0.1, 400);
      const rig = new THREE.Group();
      rig.rotation.set(0.22, 0.45, 0);
      scene.add(rig);

      const grid = new THREE.GridHelper(60, 30, ACCENT, ACCENT);
      own(grid.geometry);
      own(grid.material);
      grid.material.opacity = 0.13;
      grid.material.transparent = true;
      grid.position.y = -11;
      rig.add(grid);

      const box = own(new THREE.BoxGeometry(1, 1, 1));
      const fill = own(new THREE.InstancedMesh(
        box,
        own(new THREE.MeshBasicMaterial({ color: ACCENT, transparent: true, opacity: 0.18 })),
        N
      ));
      const wire = own(new THREE.InstancedMesh(
        box,
        own(new THREE.MeshBasicMaterial({ color: ACCENT, wireframe: true, transparent: true, opacity: 0.9 })),
        N
      ));
      fill.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      wire.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      fill.frustumCulled = wire.frustumCulled = false;
      rig.add(fill, wire);

      const pr = rng(31);
      const pp = new Float32Array(PN * 3);
      for (let i = 0; i < PN; i++) {
        const a = i * 2.399;
        const y = 1 - 2 * (i / PN);
        const rr = Math.sqrt(Math.max(0, 1 - y * y)) * (7 + pr() * 3.2);
        pp[i * 3] = Math.cos(a) * rr;
        pp[i * 3 + 1] = y * (8 + pr() * 2);
        pp[i * 3 + 2] = Math.sin(a) * rr;
      }
      const pg = own(new THREE.BufferGeometry());
      pg.setAttribute('position', new THREE.BufferAttribute(pp, 3));
      const cloud = new THREE.Points(
        pg,
        own(new THREE.PointsMaterial({ color: DEEP, size: 0.085, transparent: true, opacity: 0 }))
      );
      rig.add(cloud);

      const formations = cfg.formations();
      const cur = formations[0].map((d) => ({ p: d.p.slice(), s: d.s.slice(), q: d.q.slice(), c: d.c }));
      let cz = cfg.CAM[boundedStage(apiRef.current.stage, modelKey)] * apiRef.current.cameraScale;
      let framingScale = 1;
      let zoomOffset = 0;
      let cop = 0;
      let ccs = 1;
      let ccx = 0;
      const matrix = new THREE.Matrix4();
      const euler = new THREE.Euler();
      const quaternion = new THREE.Quaternion();
      const position = new THREE.Vector3();
      const scale = new THREE.Vector3();
      const cA = new THREE.Color(ACCENT);
      const cG = new THREE.Color(GRAY);
      const cHit = new THREE.Color(0x1d2d3d);
      const color = new THREE.Color();
      const ray = new THREE.Raycaster();
      const mouse = new THREE.Vector2();
      const t0 = performance.now();
      let pointerDown = null;

      const requestRender = () => {
        if (!dead && !failed && apiRef.current.active && !document.hidden && !raf) raf = requestAnimationFrame(tick);
      };
      const cameraBase = () => cfg.CAM[boundedStage(apiRef.current.stage, modelKey)];
      const cameraDistance = () => Math.max(14, Math.min(60, cameraBase() + zoomOffset));
      const fitViewport = () => {
        // Preserve horizontal breathing room in narrow viewers without changing the user's zoom.
        const nextScale = apiRef.current.interactionMode === 'viewer' ? Math.max(1, 1.6 / cam.aspect) : 1;
        cz *= nextScale / framingScale;
        framingScale = nextScale;
      };
      let lastView = null;
      const publishView = (force = false) => {
        const zoom = cameraBase() / cameraDistance();
        const currentStage = apiRef.current.stage;
        if (force || !lastView || lastView.zoom !== zoom || lastView.stage !== currentStage) {
          lastView = { zoom, stage: currentStage };
          apiRef.current.onViewChange?.({ zoom });
        }
      };
      controlsRef.current = {
        zoomBy(factor) {
          if (!Number.isFinite(factor) || factor <= 0) return;
          zoomOffset = Math.max(14, Math.min(60, cameraDistance() / factor)) - cameraBase();
          publishView();
          requestRender();
        },
        resetView() {
          rig.rotation.set(0.22, 0.45, 0);
          zoomOffset = 0;
          apiRef.current.selected = -1;
          publishView(true);
          requestRender();
        },
        rotateBy(yawDelta, pitchDelta = 0) {
          if (!Number.isFinite(yawDelta) || !Number.isFinite(pitchDelta)) return;
          rig.rotation.y += yawDelta;
          rig.rotation.x = Math.max(-1.15, Math.min(1.15, rig.rotation.x + pitchDelta));
          requestRender();
        },
        selectSample(index) {
          apiRef.current.selected = Number.isInteger(index) && index >= 0 && index < N ? index : -1;
          requestRender();
        },
      };
      renderRef.current = () => {
        canvas.style.touchAction = apiRef.current.interactionMode === 'viewer' ? 'none' : 'pan-y';
        fitViewport();
        publishView();
        if (apiRef.current.active) {
          requestRender();
        } else {
          cancelAnimationFrame(raf);
          raf = 0;
          pointerDown = null;
          canvas.style.cursor = 'grab';
        }
      };

      const pickId = (event) => {
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return -1;
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        if (Math.abs(mouse.x) > 1 || Math.abs(mouse.y) > 1) return -1;
        cam.updateMatrixWorld();
        rig.updateMatrixWorld(true);
        // The current formation can extend beyond an earlier stage's bounds.
        fill.computeBoundingSphere();
        ray.setFromCamera(mouse, cam);
        const hit = ray.intersectObject(fill, false).find((item) =>
          Number.isInteger(item.instanceId) && item.instanceId >= 0 && item.instanceId < N
        );
        return hit?.instanceId ?? -1;
      };
      const onPointerDown = (event) => {
        if (event.button !== 0 || pointerDown) return;
        pointerDown = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
        try { canvas.setPointerCapture?.(event.pointerId); } catch { /* Pointer already ended. */ }
        canvas.style.cursor = 'grabbing';
      };
      const onPointerMove = (event) => {
        if (pointerDown?.id !== event.pointerId) return;
        const dx = event.clientX - pointerDown.x;
        const dy = event.clientY - pointerDown.y;
        if (Math.abs(dx) + Math.abs(dy) > 6) pointerDown.moved = true;
        if (!pointerDown.moved) return;
        rig.rotation.y += dx * 0.008;
        rig.rotation.x = Math.max(-1.15, Math.min(1.15, rig.rotation.x + dy * 0.006));
        pointerDown.x = event.clientX;
        pointerDown.y = event.clientY;
        requestRender();
      };
      const onPointerUp = (event) => {
        if (pointerDown?.id !== event.pointerId) return;
        const start = pointerDown;
        pointerDown = null;
        try { canvas.releasePointerCapture?.(event.pointerId); } catch { /* Capture already released. */ }
        canvas.style.cursor = 'grab';
        if (start.moved) return;
        const id = pickId(event);
        apiRef.current.selected = id;
        if (id >= 0) apiRef.current.onPick?.(inspectParticle(modelKey, apiRef.current.stage, id));
        else if (apiRef.current.interactionMode === 'viewer') apiRef.current.onPick?.(null);
        requestRender();
      };
      const onPointerCancel = (event) => {
        if (pointerDown?.id !== event.pointerId) return;
        pointerDown = null;
        canvas.style.cursor = 'grab';
      };
      const onWheel = (event) => {
        if (apiRef.current.interactionMode !== 'viewer' && !(event.ctrlKey || event.metaKey)) return;
        event.preventDefault();
        const base = cameraBase();
        zoomOffset = Math.max(14, Math.min(60, base + zoomOffset + event.deltaY * 0.02)) - base;
        publishView();
        requestRender();
      };
      listen(canvas, 'pointerdown', onPointerDown);
      listen(window, 'pointermove', onPointerMove);
      listen(window, 'pointerup', onPointerUp);
      listen(window, 'pointercancel', onPointerCancel);
      listen(canvas, 'lostpointercapture', onPointerCancel);
      listen(canvas, 'wheel', onWheel, { passive: false });
      listen(canvas, 'webglcontextlost', (event) => {
        event.preventDefault();
        showFallback();
      });
      listen(document, 'visibilitychange', () => {
        if (document.hidden) {
          cancelAnimationFrame(raf);
          raf = 0;
          pointerDown = null;
          canvas.style.cursor = 'grab';
        } else requestRender();
      });
      const onMotionChange = (event) => {
        reducedMotion = event.matches;
        requestRender();
      };
      if (motionQuery?.addEventListener) listen(motionQuery, 'change', onMotionChange);
      else if (motionQuery?.addListener) {
        motionQuery.addListener(onMotionChange);
        removers.push(() => motionQuery.removeListener(onMotionChange));
      }

      const resize = () => {
        if (dead || failed) return;
        const width = host.clientWidth || 800;
        const height = host.clientHeight || 600;
        renderer.setSize(width, height, false);
        cam.aspect = width / height;
        cam.updateProjectionMatrix();
        fitViewport();
        requestRender();
      };
      publishView();
      resize();
      if (typeof ResizeObserver === 'function') {
        observer = new ResizeObserver(resize);
        observer.observe(host);
      } else listen(window, 'resize', resize);

      function tick() {
        raf = 0;
        if (dead || failed || !apiRef.current.active || document.hidden) return;
        const s = boundedStage(apiRef.current.stage, modelKey);
        const target = formations[s];
        const t = (performance.now() - t0) / 1000;
        const flowing = (modelKey === 'fabric' && s === 3) || (modelKey === 'overview' && s === 4);
        const flow = !reducedMotion && flowing ? (t * 2.2) % 1.05 : 0;
        const selected = apiRef.current.selected;
        for (let i = 0; i < N; i++) {
          const c = cur[i];
          const d = target[i];
          const k = reducedMotion ? 1 : 0.075;
          for (let j = 0; j < 3; j++) {
            c.p[j] += (d.p[j] - c.p[j]) * k;
            c.s[j] += (d.s[j] - c.s[j]) * k;
            c.q[j] += (d.q[j] - c.q[j]) * k;
          }
          c.c += (d.c - c.c) * k;
          euler.set(c.q[0], c.q[1], c.q[2]);
          quaternion.setFromEuler(euler);
          position.set(c.p[0] + flow, c.p[1], c.p[2]);
          const pop = i === selected ? 1.4 : 1;
          scale.set(Math.max(0.02, c.s[0] * pop), Math.max(0.02, c.s[1] * pop), Math.max(0.02, c.s[2] * pop));
          matrix.compose(position, quaternion, scale);
          fill.setMatrixAt(i, matrix);
          wire.setMatrixAt(i, matrix);
          if (i === selected) color.copy(cHit);
          else color.copy(cG).lerp(cA, Math.max(0, Math.min(1, c.c)));
          fill.setColorAt(i, color);
          wire.setColorAt(i, color);
        }
        fill.instanceMatrix.needsUpdate = wire.instanceMatrix.needsUpdate = true;
        if (fill.instanceColor) fill.instanceColor.needsUpdate = true;
        if (wire.instanceColor) wire.instanceColor.needsUpdate = true;

        const cloudStage = cfg.CLOUD[s];
        cop += (cloudStage.o - cop) * (reducedMotion ? 1 : 0.06);
        ccs += (cloudStage.s - ccs) * (reducedMotion ? 1 : 0.06);
        ccx += (cloudStage.c[0] - ccx) * (reducedMotion ? 1 : 0.06);
        cloud.material.opacity = cop;
        cloud.scale.setScalar(ccs);
        cloud.position.x = ccx;
        const distance = cameraDistance() * apiRef.current.cameraScale * framingScale;
        cz += (distance - cz) * (reducedMotion ? 1 : 0.045);
        cam.position.set(0, 0, cz);
        cam.lookAt(0, 0, 0);
        try { renderer.render(scene, cam); } catch { showFallback(); }
        if (!reducedMotion) requestRender();
      }
    };

    setUnavailable(false);
    try { boot(); } catch { showFallback(); }
    return () => {
      dead = true;
      dispose();
    };
  }, [modelKey]);

  useEffect(() => {
    apiRef.current.selected = -1;
  }, [stage]);

  useEffect(() => {
    renderRef.current?.();
  }, [stage, active, safeCameraScale, interactionMode]);

  return (
    <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <div ref={hostRef} aria-hidden="true" style={{ width: '100%', height: '100%' }} />
      {unavailable && (
        <div role="status" style={{ position: 'absolute', top: '18%', left: '50%', transform: 'translateX(-50%)', width: 'min(80%, 400px)', textAlign: 'center', color: 'var(--color-neutral-700, #414b56)', fontSize: 14, lineHeight: 1.6 }}>
          <strong>3D 시각화를 사용할 수 없습니다.</strong><br />
          단계 설명과 데이터 목록에서 모델을 계속 탐색할 수 있습니다.
        </div>
      )}
    </div>
  );
});

export default ModelPipeline3D;
